import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useConversationTitle = (threadId: string | null, defaultTitle: string) => {
  const [conversationTitle, setConversationTitle] = useState<string>(defaultTitle);

  const fetchTitle = async (threadId: string) => {
    try {
      // First try to query with all columns
      try {
        const { data, error } = await supabase
          .from('chat_conversations')
          .select('*')
          .eq('thread_id', threadId)
          .single();
        
        if (error) {
          console.warn("Full query error, trying with basic columns:", error);
          throw error;
        }
        
        handleConversationData(data);
      } catch (fullQueryError) {
        // If full query fails, try with only basic columns
        try {
          const { data, error } = await supabase
            .from('chat_conversations')
            .select('thread_id, created_at, updated_at')
            .eq('thread_id', threadId)
            .single();
            
          if (error) {
            console.error("Basic query error:", error);
            throw error;
          }
          
          handleConversationData(data);
        } catch (basicQueryError) {
          console.error("All queries failed:", basicQueryError);
          setConversationTitle(defaultTitle);
        }
      }
    } catch (err) {
      console.error("Exception fetching conversation title:", err);
      setConversationTitle(defaultTitle);
    }
  };
  
  // Helper to process the conversation data
  const handleConversationData = (data: any) => {
    if (!data) {
      setConversationTitle(defaultTitle);
      return;
    }
    
    // If title exists in data, use it
    if (data.title) {
      setConversationTitle(data.title);
    } else {
      // Otherwise generate a title from the date
      const updatedAt = data.updated_at 
        ? new Date(data.updated_at) 
        : data.created_at 
          ? new Date(data.created_at)
          : new Date();
      
      const formattedDate = formatDateToNorwegian(updatedAt);
      setConversationTitle(`Samtale ${formattedDate}`);
    }
  };

  // Helper function to format date in Norwegian
  const formatDateToNorwegian = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}.${month}.${year} kl. ${hours}:${minutes}`;
  };

  useEffect(() => {
    if (threadId) {
      fetchTitle(threadId);
    } else {
      setConversationTitle(defaultTitle);
    }
  }, [threadId, defaultTitle]);

  return { conversationTitle };
};
